import { useTranslation } from "react-i18next";
import { Maximize, Minimize } from "lucide-react";
import { Button, DraggableWindow } from "@tellescope/ui";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useBackgroundEffect } from "../hooks/useBackgroundEffect";
import { WaitingRoomView } from "./WaitingRoomView";
import { VideoRoomView } from "./VideoRoomView";
import type { VideoCallWindowProps } from "../types";

/**
 * VideoCallWindow - Main draggable window component for video calls.
 *
 * Displays waiting room with preview or active video call UI.
 * Uses the DraggableWindow component for positioning and drag behavior.
 *
 * This component should be rendered at the app root level (like toasts)
 * and will automatically show/hide based on the isVisible state.
 *
 * @example
 * ```tsx
 * // In apps/web/src/providers/VideoCallingProvider.tsx
 * export function VideoCallingProvider({ children }) {
 *   const videoCalling = useVideoCalling();
 *
 *   return (
 *     <VideoCallingHookProvider useVideoCalling={useVideoCalling} session={session}>
 *       {children}
 *       <VideoCallWindow
 *         userRole={videoCalling.meetingMeta.isHost ? "host" : "participant"}
 *         showEndForAll={videoCalling.meetingMeta.isHost}
 *         resolveParticipantIdentity={(id) => usersById[id]?.displayName}
 *       />
 *     </VideoCallingHookProvider>
 *   );
 * }
 * ```
 */
export function VideoCallWindow({
  userRole,
  showEndForAll = false,
  title,
  localUser,
  resolveParticipantIdentity,
  backgroundImageURL,
  className,
}: VideoCallWindowProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();

  useBackgroundEffect(backgroundImageURL);

  const { isVisible, isWaitingRoom, isFullscreen, meetingMeta, connectionState } = videoCalling;

  if (!isVisible) {
    return null;
  }

  // Determine window size based on state
  const isConnected = connectionState === "connected";
  const width = isConnected && !isWaitingRoom ? 640 : 400;
  const height = isConnected && !isWaitingRoom ? 480 : undefined;

  return (
    <DraggableWindow
      open={isVisible}
      title={title ?? meetingMeta.eventTitle ?? t("Video Call")}
      persistKey="video-call-window"
      minimizable
      width={width}
      height={height}
      fullscreen={isFullscreen}
      initialPosition="center"
      constrainToViewport
      viewportPadding={20}
      showCloseButton={false}
      className={className}
      contentClassName="flex flex-col !overflow-hidden"
      headerActions={
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => videoCalling.setIsFullscreen(!isFullscreen)}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={isFullscreen ? t("Exit fullscreen") : t("Enter fullscreen")}
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </Button>
      }
    >
      {isWaitingRoom ? (
        <WaitingRoomView userRole={userRole} localUser={localUser} />
      ) : (
        <VideoRoomView
          userRole={userRole}
          showEndForAll={showEndForAll}
          localUser={localUser}
          resolveParticipantIdentity={resolveParticipantIdentity}
        />
      )}
    </DraggableWindow>
  );
}
